// src/components/sections/ResourcesSection.js
import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaBookMedical, FaQuestionCircle, FaNewspaper,
    FaVideo, FaFilePdf, FaDownload, FaChevronDown, FaChevronUp
} from 'react-icons/fa';

const ResourcesSection = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const resources = [
        {
            category: "Pregnancy & Parenting",
            items: [
                { title: "Pregnancy Nutrition Guide", type: "PDF", icon: <FaFilePdf />, color: "#FF7F50" },
                { title: "Newborn Care Video Series", type: "Video", icon: <FaVideo />, color: "#87CEEB" },
                { title: "Postpartum Recovery Tips", type: "Article", icon: <FaNewspaper />, color: "#9370DB" }
            ]
        },
        {
            category: "Women's Health",
            items: [
                { title: "Menstrual Health Handbook", type: "PDF", icon: <FaFilePdf />, color: "#20B2AA" },
                { title: "Breast Cancer Awareness", type: "Article", icon: <FaNewspaper />, color: "#FFD700" },
                { title: "Fertility Treatment Options", type: "Guide", icon: <FaBookMedical />, color: "#3CB371" }
            ]
        },
        {
            category: "Child Health",
            items: [
                { title: "Vaccination Schedule", type: "PDF", icon: <FaFilePdf />, color: "#DC143C" },
                { title: "Child Development Milestones", type: "Interactive", icon: <FaVideo />, color: "#FF6347" },
                { title: "Nutrition for Growing Kids", type: "Article", icon: <FaNewspaper />, color: "#20B2AA" }
            ]
        }
    ];

    const faqs = [
        {
            question: "What should I bring for my hospital stay during delivery?",
            answer: "Bring comfortable clothing, toiletries, insurance documents, birth plan (if any), and items for the baby. We provide a detailed packing list during your prenatal visits.",
            category: "Pregnancy"
        },
        {
            question: "How do I schedule an appointment with a specialist?",
            answer: "You can schedule appointments online through our patient portal, call our appointment desk, or use the WhatsApp booking service. New patient forms are available online.",
            category: "General"
        },
        {
            question: "What insurance plans do you accept?",
            answer: "We accept most major insurance providers including Aetna, Blue Cross, Cigna, UnitedHealthcare, and Medicaid. Contact our billing department for specific plan verification.",
            category: "Billing"
        },
        {
            question: "Are visitors allowed in the NICU?",
            answer: "Yes, parents have 24/7 access. Other visitors are allowed during designated hours with proper screening. We follow strict infection control protocols.",
            category: "NICU"
        },
        {
            question: "What COVID-19 safety measures are in place?",
            answer: "We follow CDC guidelines including mask requirements, visitor restrictions, enhanced cleaning, and vaccination requirements for staff. Current protocols are updated on our website.",
            category: "Safety"
        }
    ];

    const handleFaqToggle = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    return (
        <section id="resources" className="section-padding">
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Educational Resources & Support
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Comprehensive health information and support for informed healthcare decisions
                    </p>
                </motion.div>

                {/* Resources Grid */}
                <Row className="g-4 mb-5">
                    {resources.map((resourceGroup, groupIndex) => (
                        <Col lg={4} md={6} key={groupIndex}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Card className="healthcare-card border-0 h-100">
                                    <Card.Body className="p-4">
                                        <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                                            {resourceGroup.category}
                                        </h4>

                                        <div className="mb-4">
                                            {resourceGroup.items.map((item, itemIndex) => (
                                                <div
                                                    key={itemIndex}
                                                    className="d-flex align-items-center justify-content-between p-3 rounded mb-3 hover-lift"
                                                    style={{
                                                        backgroundColor: `${item.color}10`,
                                                        borderLeft: `4px solid ${item.color}`,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                backgroundColor: `${item.color}20`,
                                                                color: item.color,
                                                                fontSize: '1rem'
                                                            }}
                                                        >
                                                            {item.icon}
                                                        </div>
                                                        <div>
                                                            <h6 className="fw-bold mb-1" style={{ color: 'var(--dark-teal)' }}>
                                                                {item.title}
                                                            </h6>
                                                            <small className="text-muted">{item.type}</small>
                                                        </div>
                                                    </div>
                                                    <FaDownload style={{ color: item.color }} />
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            className="btn btn-link text-decoration-none p-0 fw-bold"
                                            style={{ color: 'var(--primary-teal)' }}
                                        >
                                            View All {resourceGroup.category} Resources →
                                        </button>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* FAQs Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="mb-5"
                >
                    <div className="text-center mb-5">
                        <h3 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                            Frequently Asked Questions
                        </h3>
                        <p className="text-muted">
                            Find answers to common questions about our services and procedures
                        </p>
                    </div>

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Accordion defaultActiveKey="0">
                                {faqs.map((faq, index) => (
                                    <Accordion.Item
                                        key={index}
                                        eventKey={index.toString()}
                                        className="mb-3 border-0"
                                    >
                                        <Accordion.Header
                                            className="healthcare-card p-0 border-0"
                                            style={{
                                                backgroundColor: openFaq === index ? 'var(--teal-light)' : 'white'
                                            }}
                                        >
                                            <div className="d-flex align-items-center w-100 p-4">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        backgroundColor: 'var(--primary-teal)',
                                                        color: 'white',
                                                        fontSize: '1rem',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <FaQuestionCircle />
                                                </div>
                                                <div className="text-start flex-grow-1">
                                                    <h6 className="fw-bold mb-1" style={{ color: 'var(--dark-teal)' }}>
                                                        {faq.question}
                                                    </h6>
                                                    <small className="text-muted">{faq.category}</small>
                                                </div>
                                                {openFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="p-4 pt-0">
                                            <div className="ps-5">
                                                <p className="mb-0" style={{ color: '#666' }}>
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Col>
                    </Row>
                </motion.div>

                {/* Newsletter & Updates */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                >
                    <div
                        className="p-5 rounded text-center"
                        style={{
                            background: 'linear-gradient(135deg, var(--teal-light) 0%, white 100%)',
                            border: '2px dashed var(--primary-teal)'
                        }}
                    >
                        <div className="row align-items-center">
                            <Col lg={6} className="mb-4 mb-lg-0">
                                <h4 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                    Stay Informed with Health Updates
                                </h4>
                                <p className="mb-0" style={{ color: '#666' }}>
                                    Subscribe to our newsletter for health tips, event announcements,
                                    and the latest medical insights
                                </p>
                            </Col>

                            <Col lg={6}>
                                <div className="input-group">
                                    <input
                                        type="email"
                                        className="form-control form-control-lg"
                                        placeholder="Enter your email address"
                                        style={{ 
                                            borderRadius: '50px 0 0 50px',
                                            fontSize: '0.9rem' // Reduced font size
                                        }}
                                    />
                                    <button
                                        className="btn btn-primary-teal px-4"
                                        style={{ borderRadius: '0 50px 50px 0' }}
                                    >
                                        Subscribe
                                    </button>
                                </div>
                                <small className="text-muted mt-2 d-block">
                                    We respect your privacy. Unsubscribe at any time.
                                </small>
                            </Col>
                        </div>
                    </div>
                </motion.div>

                {/* Additional Resources Links */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-5 pt-4"
                >
                    <Row className="g-3 justify-content-center">
                        <Col xs="auto">
                            <Button
                                variant="outline-primary"
                                className="px-4"
                                style={{ borderRadius: '50px' }}
                            >
                                Patient Education Portal
                            </Button>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-primary"
                                className="px-4"
                                style={{ borderRadius: '50px' }}
                            >
                                Support Groups
                            </Button>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-primary"
                                className="px-4"
                                style={{ borderRadius: '50px' }}
                            >
                                Research Publications
                            </Button>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-primary"
                                className="px-4"
                                style={{ borderRadius: '50px' }}
                            >
                                Community Events
                            </Button>
                        </Col>
                    </Row>
                </motion.div>
            </Container>
        </section>
    );
};

export default ResourcesSection;